// Упоминание — ячейка «Ответственный» или «Подрядчик», делённая на имена (005: FR-001 – FR-003,
// data-model.md, directory.mentions). Текст — как в файле (принцип I); запись справочника
// предлагает разбор, решает человек (принцип III).
import { z } from '../kernel/zod.ts';
import { DateSchema, IdSchema, TimestampSchema } from '../kernel/common.ts';
import { AddresseeSchema } from './addressee.ts';
import { RecordRefSchema, UnitRefSchema } from './record.ts';

/**
 * Вид упоминания; 006: подразделение («ПТО») — связано с подразделением, должность («ГИП») —
 * с человеком, у которого место работы с этой должностью в зоне документа.
 */
export const mentionKinds = [
  'person',
  'organization',
  'unclear',
  'not_addressee',
  'unit',
  'position',
] as const;
export type MentionKind = (typeof mentionKinds)[number];
export const MentionKindSchema = z.enum(mentionKinds);

export const mentionKindLabels = {
  person: 'Человек',
  organization: 'Организация',
  unclear: 'Неясно',
  not_addressee: 'Не адресат',
  unit: 'Подразделение',
  position: 'Должность',
} as const satisfies Record<MentionKind, string>;

/**
 * new — ещё не связано, candidate — связано с вопросом (предложенный псевдоним, однофамилец,
 * человек из архива), confirmed — связано без вопроса или решением человека, rejected — «не он».
 */
export const mentionStates = ['new', 'candidate', 'confirmed', 'rejected'] as const;
export type MentionState = (typeof mentionStates)[number];
export const MentionStateSchema = z.enum(mentionStates);

/**
 * Почему упоминание связано с вопросом (FR-006, FR-007, FR-007а): однофамильцы без однозначного
 * контекста, человек в архиве, переведён из другого юрлица, контекст не совпал, предложенный
 * псевдоним, у документа нет зоны ответственности.
 */
export const mentionQuestions = [
  'ambiguous',
  'archived',
  'transferred',
  'context',
  'suggested',
  'no_zone',
  'position',
] as const;
export type MentionQuestion = (typeof mentionQuestions)[number];
export const MentionQuestionSchema = z.enum(mentionQuestions);

export const mentionQuestionLabels = {
  ambiguous: 'Несколько записей подходят — выберите нужную',
  archived: 'Запись в архиве — вернуть её или это другой человек?',
  transferred: 'Похоже, человек переведён из другого юрлица',
  context: 'Написание знакомо, но в другом контексте',
  suggested: 'Написание предложено, но не подтверждено',
  no_zone: 'У документа нет зоны ответственности — однофамильцев не различить',
  position: 'Кто это по должности в этом документе?',
} as const satisfies Record<MentionQuestion, string>;

/** Колонка листа, из которой взято упоминание (003: responsibleFrom, колонка «Подрядчик»). */
export const mentionColumns = ['responsible', 'contractor'] as const;
export type MentionColumn = (typeof mentionColumns)[number];
export const MentionColumnSchema = z.enum(mentionColumns);

/** Контекст на момент связывания (FR-003): документ, раздел, юрлицо зоны документа. */
export const MentionContextSchema = z.object({
  documentId: IdSchema,
  section: z.string(),
  legalEntityId: IdSchema.nullable(),
});
export type MentionContext = z.infer<typeof MentionContextSchema>;

/**
 * Решение ИИ о виде упоминания (005-3: FR-006, FR-011; сценарий 2, п. 4): applied — вид
 * «организация» задан решением ИИ и человек упоминание не решал (пометка «вид — ИИ»); иначе —
 * подсказка «скорее организация» или «скорее человек». Вход — текст упоминания и колонка строки.
 */
export const MentionKindAiSchema = z.object({
  /** Вероятность «организация». */
  probability: z.number().min(0).max(1),
  applied: z.boolean(),
  model: z.string(),
  questionVersion: z.string(),
  at: TimestampSchema,
});
export type MentionKindAi = z.infer<typeof MentionKindAiSchema>;

/**
 * Упоминание строкой протокола (FR-010): где записано, текст ячейки целиком с выделенным
 * упоминанием, запись и адресат.
 */
export const MentionRowSchema = z.object({
  id: IdSchema,
  documentId: IdSchema,
  documentNumber: z.number().int().min(1),
  versionId: IdSchema,
  sheetName: z.string(),
  sheetDate: DateSchema,
  row: z.number().int().min(1),
  column: MentionColumnSchema,
  /** Порядковый номер в ячейке, с 0: «Киселёв, Дорофеева» — 0 и 1. */
  ordinal: z.number().int().min(0),
  /** Текст упоминания как записан. */
  text: z.string(),
  /** Ячейка целиком — для выделения упоминания в строке протокола. */
  cellText: z.string(),
  kind: MentionKindSchema,
  state: MentionStateSchema,
  record: RecordRefSchema.nullable(),
  /** Вопрос контролёру при state = candidate; рекомендованная запись — record. */
  question: MentionQuestionSchema.nullable(),
  context: MentionContextSchema,
  /** Строка протокола: раздел, номер, текст пункта и срок как в файле. */
  item: z.object({
    section: z.string(),
    number: z.string().nullable(),
    text: z.string(),
    dueRaw: z.string().nullable(),
    open: z.boolean(),
  }),
  addressee: AddresseeSchema,
  /** Подразделение упоминания вида «подразделение» (006); иначе null. */
  unit: UnitRefSchema.nullable(),
  /** Решение ИИ о виде (005-3); модель не спрашивали — null. */
  kindAi: MentionKindAiSchema.nullable(),
});
export type MentionRow = z.infer<typeof MentionRowSchema>;
